import type { AutomaticTranscriptsView } from '../api/client';
import { zoneCity } from './time';

/**
 * Stitch Admin → System → Call records: what the Transcription rows say about automatic recording
 * transcripts. Plain words; the numbers come from the API (Postgres for what's waiting and done,
 * the bulk lane for what's being written out).
 */

const n = (x: number) => x.toLocaleString('en-US');

/** "7:00 AM" from "07:00". */
export function clockLabel(clock: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(clock);
  if (!m) return clock;
  const h = Number(m[1]);
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}

/** "about 40 minutes", "about 3 hours", "about 2 days". */
export function clearLabel(minutes: number): string {
  if (minutes < 55) return `about ${Math.max(5, Math.round(minutes / 5) * 5)} minutes`;
  const hours = Math.max(1, Math.round(minutes / 60));
  if (hours < 48) return `about ${hours} hour${hours === 1 ? '' : 's'}`;
  return `about ${Math.round(hours / 24)} days`;
}

/** "8:00 PM to 7:00 AM, New York time". */
export function nightLabel(w: { nightStart: string; nightEnd: string; zone: string }): string {
  return `${clockLabel(w.nightStart)} to ${clockLabel(w.nightEnd)}, ${zoneCity(w.zone)} time`;
}

export interface AutomaticLines {
  /** "1,357 waiting, 40 of them in line · 1 being written out · 12 finished in the last hour" */
  main: string;
  /** Smaller lines under it. */
  notes: string[];
  /** Chips beside it: catching up at night, or nothing finishing. */
  chips: { tone: 'ok' | 'warn' | 'crit' | 'brand'; text: string }[];
}

export function automaticLines(a: AutomaticTranscriptsView): AutomaticLines {
  const parts = [a.owed ? `${n(a.owed)} waiting, ${n(a.inLine)} of them in line` : 'None waiting'];
  if (a.active !== null) parts.push(`${n(a.active)} being written out`);
  parts.push(`${n(a.doneLastHour)} finished in the last hour`);
  const notes: string[] = [];
  if (a.failedTonight) notes.push(`${n(a.failedTonight)} failed, to be tried again tonight`);
  if (a.failedForGood) notes.push(`${n(a.failedForGood)} couldn’t be written out (gone from the phone system, too long, or tried on three nights)`);
  if (a.owed && a.clearMinutes !== null) notes.push(`A${clearLabel(a.clearMinutes).slice(1)} to clear at this pace`);
  if (a.lastNight && (a.lastNight.retried || a.lastNight.found)) {
    notes.push(`The latest night’s catch-up: ${n(a.lastNight.retried)} tried again, ${n(a.lastNight.found)} missed recordings found`);
  }
  const chips: AutomaticLines['chips'] = [];
  if (a.night.now.inWindow) chips.push({ tone: 'brand', text: `Catching up (night, until ${clockLabel(a.night.nightEnd)})` });
  // Waiting, but nothing finished for half an hour: red when nothing is being written out either.
  if (a.stalled) chips.push({ tone: a.active ? 'warn' : 'crit', text: 'Nothing finished in the last 30 minutes' });
  return { main: parts.join(' · '), notes, chips };
}
